import { useMutation } from '@apollo/client/react';
import { App } from 'antd';
import { useAuth } from 'react-oidc-context';
import { fetchPrintable, NO_PRINT_PATH_HINT } from '../utils/print';
import { printErrorShown, usePrinter } from './usePrinter';
import { MARK_PRINTED_MUTATION } from './usePrintRequest.gql';

type PrintableRequest = {
  id: string;
  orders: Array<{
    id: string;
    orderLabels: Array<{
      id: string;
      printedAt?: string | null;
      specimenId: string;
      testTubeId: string;
      location: string;
      specialPrinterRequired: boolean;
    }>;
    orderFiles: Array<{
      id: string;
      fileName: string;
      printable: boolean;
      printRequired: boolean;
      printedAt?: string | null;
      printBeforeSend: boolean;
      printCopies: number;
    }>;
  }>;
};

type PrintFn = (
  mode: 'before-send' | 'send',
  request: PrintableRequest,
  onError: () => void,
  onNoPrint: () => void,
  onSuccess: () => void,
  onMarkError: () => void,
  onRefetch: () => void
) => Promise<boolean>;

// the labels and documents the lab settings ask for: unprinted, and before sending only those due before
const toPrint = (mode: 'before-send' | 'send', request: PrintableRequest) => ({
  labels: request.orders.flatMap(it => it.orderLabels).filter(it => !it.printedAt),
  files: request.orders.flatMap(order =>
    order.orderFiles
      .filter(it => it.printable && it.printRequired && !it.printedAt)
      .filter(it => (mode === 'before-send' ? it.printBeforeSend : true))
      .map(file => ({ ...file, orderId: order.id }))
  ),
});

/** Prints what the desktop client prints for an Anforderung, then marks it printed; true when labels were printed. */
export const usePrintRequest = (): PrintFn => {
  const auth = useAuth();
  const { message } = App.useApp();
  const { canPrint, print, printLabels } = usePrinter();
  const [markPrintedMutation] = useMutation(MARK_PRINTED_MUTATION);

  return async (mode, request, onError, onNoPrint, onSuccess, onMarkError, onRefetch) => {
    const { labels, files } = toPrint(mode, request);
    if (!canPrint) {
      // a send still goes out; it just must not look as if anything was printed
      if (labels.length + files.length > 0) {
        message.warning(`Etiketten und Dokumente wurden nicht gedruckt. ${NO_PRINT_PATH_HINT}`);
      }
      return false;
    }

    let error = false;
    let labelsPrinted = false;
    try {
      const complete = await printLabels(labels, { requestId: request.id });
      error = !complete;
      labelsPrinted = complete && labels.length > 0;
    } catch (e) {
      // the AIS not printing one job would not print the next either, nor the extra labels: one message, the rest left out
      if (printErrorShown(e)) {
        return false;
      }
      error = true;
    }

    for (const file of files) {
      try {
        const buffer = await fetchPrintable(`/rest/order-file/${file.id}`, auth.user?.access_token);
        await print(buffer, 'a4', {
          title: file.fileName,
          copies: file.printCopies,
          requestId: request.id,
          orderId: file.orderId,
        });
      } catch (e) {
        if (printErrorShown(e)) {
          return false;
        }
        error = true;
      }
    }

    if (error) {
      onError();
      return labelsPrinted;
    }

    if (labels.length + files.length < 1) {
      onNoPrint();
    } else {
      onSuccess();
    }

    try {
      await markPrintedMutation({ variables: { requestId: request.id, beforeSend: mode === 'before-send' } });
      onRefetch();
    } catch {
      onMarkError();
    }

    return labelsPrinted;
  };
};
